import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import { IPurchaseStateWithCustomerRelationship } from "@smartrr/shared/entities/PurchaseState";
import { IShopifyLiquidSellingPlanWithGroupIdAndName } from "@smartrr/shared/shopifyLiquid/selling_plan_group";
import { useActiveOrganizationSelector } from "@vendor-app/app/_state/reducers/organizations";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useSellingPlans } from "../../../libs/providers/SellingPlansProvider";
import { useUpdateSellingPlan } from "../../components/SubscriptionTabs/hooks/useUpdateSellingPlan";
import { ConfirmSellingPlanModalProps } from "./index";

export const useConfirmSellingPlanModal = (
  customerPurchaseState: IPurchaseStateWithCustomerRelationship,
  sellingPlanOptions: IShopifyLiquidSellingPlanWithGroupIdAndName[]
): {
  sellingPlanUpdating: boolean;
  confirmSellingPlanModalProps: ConfirmSellingPlanModalProps | null;
} => {
  const { selectedSellingPlanId, setSelectedSellingPlanId } = useSellingPlans();
  const organization = useActiveOrganizationSelector();
  const [sellingPlanUpdating, setSellingPlanUpdating] = useState(false);
  const [nextOrderDatePreviewLabel, setNextOrderDatePreviewLabel] = useState<string>("——");
  const [isLoading, setIsLoading] = useState(false);

  const onUpdateSellingPlan = useUpdateSellingPlan(
    customerPurchaseState,
    () => setSellingPlanUpdating(true),
    () => setSellingPlanUpdating(false)
  );

  const pendingPlanName = useMemo(() => {
    return sellingPlanOptions.find(plan => plan.id === selectedSellingPlanId)?.name;
  }, [selectedSellingPlanId, sellingPlanOptions]);

  useEffect(() => {
    if (!selectedSellingPlanId || !organization || !customerPurchaseState.custRel) {
      setNextOrderDatePreviewLabel("——");
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    setIsLoading(true);

    void typedFrontendVendorApi
      .getReq(
        "/customer/:customerRelationshipId/purchase-state/:customerPurchaseStateId/selling-plan-next-order-date-preview",
        {
          params: {
            customerRelationshipId: customerPurchaseState.custRel.id,
            customerPurchaseStateId: customerPurchaseState.id,
          },
          query: { sellingPlanId: selectedSellingPlanId.toString() },
        }
      )
      .then(res => {
        if (cancelled) {
          return;
        }

        if (res.type !== "success") {
          setNextOrderDatePreviewLabel("——");
          return;
        }

        const upcomingDate = ISODateString.fromString(res.body.nextOrderDate).setZone(
          organization.billingTimezone,
          {
            keepLocalTime: true,
          }
        );

        setNextOrderDatePreviewLabel(
          upcomingDate.toLocaleString({
            month: "long",
            day: "2-digit",
            year: "numeric",
          })
        );
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setNextOrderDatePreviewLabel("——");
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedSellingPlanId,
    organization,
    customerPurchaseState.id,
    customerPurchaseState.custRel,
    customerPurchaseState.nextOrderDate,
    customerPurchaseState.nextBillingDate,
  ]);

  const handleClose = useCallback(() => {
    setSelectedSellingPlanId(null);
  }, [setSelectedSellingPlanId]);

  const handleConfirm = useCallback(
    async (sellingPlanId: number) => {
      await onUpdateSellingPlan(sellingPlanId);
      setSelectedSellingPlanId(null);
    },
    [onUpdateSellingPlan, setSelectedSellingPlanId]
  );

  const confirmSellingPlanModalProps = useMemo((): ConfirmSellingPlanModalProps | null => {
    if (!selectedSellingPlanId || !pendingPlanName) {
      return null;
    }
    return {
      open: true,
      planName: pendingPlanName,
      selectedSellingPlanId,
      onClose: handleClose,
      onConfirm: handleConfirm,
      nextOrderDate: nextOrderDatePreviewLabel,
      isLoading,
    };
  }, [selectedSellingPlanId, pendingPlanName, handleClose, handleConfirm, nextOrderDatePreviewLabel, isLoading]);

  return {
    sellingPlanUpdating,
    confirmSellingPlanModalProps,
  };
};
